import { type FC, type ReactNode } from "react";
import { type ProductData } from "~/domains/product/types/ProductData";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { INVALID_PACKAGE_CODE, MAX_QUANTITY } from "~/utils/constants";
import { PlainFormattedNumber } from "~/common/components/PlainFormattedNumber";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";

import { isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { Controller, useFormContext } from "react-hook-form";
import type { AddToCartFormDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { useAddToCartDerived } from "~/features/addToCart/hooks/AddToCartDerivedContext";
import { useTranslation } from "react-i18next";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { InfoOrderHandlingUnitQuantity } from "~/features/productDetails/components/ProductSummary/InfoOrderHandlingUnitQuantity";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { Stack, styled } from "~/common/mui";
import { C_ON_PRIMARY } from "~/common/components/icon/Colors";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { PdpPriceBlock } from "~/features/productDetails/components/ProductSummary/components/PdpPriceBlock";

interface Props {
  quantity: number | null;
  disabled?: boolean;
  invalidQuantity?: boolean;
  onChange?: (newQty: number) => void;

  priceReference: string;
  onPriceReferenceChange?: (newValue: string | undefined) => void;
  descriptor: ProductDescriptor;
  isPriceLoading?: boolean;

  showInfoOrderHandlingUnitQty?: boolean;
  helperLabel?: ReactNode;
  minQtyValue?: number;
}

export const AtcProductPriceInfo = (props: Props) => {
  const {
    quantity,
    disabled,
    onChange,
    invalidQuantity,

    priceReference,
    onPriceReferenceChange,
    descriptor,
    isPriceLoading,
    showInfoOrderHandlingUnitQty,
    helperLabel,
    minQtyValue,
  } = props;

  const formProps = useFormContext<AddToCartFormDraft>();

  const { t } = useTranslation();
  const { main } = useAddToCartDerived();
  const product = descriptor.base;

  const userCanSeePrice = userHasPermission(Permission.PRICE);

  const selectedPack = main.selectedPack;
  const packSize = main.selectedPackaging?.packSize ?? selectedPack?.packSize ?? 1;
  const packCode = selectedPack?.packCode ?? INVALID_PACKAGE_CODE;

  const isOnSale = useSalesHelper(descriptor).isPackageOnSale(packCode);
  const saleActive = isOnSale && (formProps?.watch("salePriceEnabled") ?? false);

  let priceSection;
  if (!userCanSeePrice) {
    priceSection = undefined;
  } else if (isPriceLoading) {
    priceSection = <CircleSpinner className={"mb-3"} />;
  } else if (descriptor && !isProductPMA(descriptor)) {
    priceSection = (
      <PdpPriceBlock
        descriptor={descriptor}
        packCode={packCode}
        quantity={quantity ?? 1}
        priceReference={priceReference}
      />
    );
  }

  let quantitySection;
  if (quantity !== null && product.quantityVisible) {
    quantitySection = (
      <Stack gap={1}>
        <QuantitySection
          packSize={packSize}
          quantity={quantity}
          onChange={onChange}
          disabled={disabled}
          invalidQuantity={invalidQuantity}
          helperLabel={helperLabel}
          minQtyValue={minQtyValue}
        />
        {showInfoOrderHandlingUnitQty && (
          <InfoOrderHandlingUnitQuantity
            preferredPackQuantity={selectedPack?.preferredPackQuantity ?? null}
            packSize={packSize}
            quantity={quantity}
          />
        )}
      </Stack>
    );
  }

  const priceReferenceSection = (
    <Stack gap={1}>
      {isOnSale && formProps && (
        <Controller
          name={"salePriceEnabled"}
          render={({ field: { value, onChange } }) => (
            <CheckboxWithLabel checked={value} onCheck={onChange} hidden={!isOnSale || !!priceReference}>
              {t("feature.salesCampaign.specialDealPrice")}
            </CheckboxWithLabel>
          )}
        />
      )}

      <PriceReferenceSection
        product={product}
        priceReference={priceReference}
        onPriceReferenceChange={onPriceReferenceChange}
        disabled={disabled || saleActive}
      />
    </Stack>
  );

  return (
    <NewVariantWrapper gap={2} data-prod-article={product.hybrisCode}>
      {priceSection}
      {quantitySection}
      {priceReferenceSection}
    </NewVariantWrapper>
  );
};

export default AtcProductPriceInfo;

//////////////////////////////////////
// QUANTITY SECTION
//////////////////////////////////////
type QuantityProps = Pick<Props, "disabled" | "onChange" | "invalidQuantity"> & {
  quantity: number;
  packSize: number;
  helperLabel?: ReactNode;
  minQtyValue?: number;
};
const QuantitySection: FC<QuantityProps> = ({
  disabled,
  quantity,
  onChange,
  invalidQuantity,
  packSize,
  helperLabel,
  minQtyValue = packSize,
}) => {
  const { t } = useTranslation();
  if (disabled) {
    return (
      <QuantityDisplay>
        <QuantityLabel className="mr-1">{t("input.quantity")}</QuantityLabel>
        <QuantityValue>
          <PlainFormattedNumber value={quantity} />
        </QuantityValue>
      </QuantityDisplay>
    );
  }

  const helperText = () => {
    if (helperLabel) {
      return helperLabel;
    } else if (invalidQuantity) {
      return (
        <span className="error">
          {t("contractDetails.atc.quantityMismatch", {
            lower: Math.floor(quantity / packSize) * packSize,
            higher: Math.ceil(quantity / packSize) * packSize,
          })}
        </span>
      );
    }
  };

  return (
    <StyledQuantityInput
      variant={"compact"}
      min={minQtyValue}
      max={MAX_QUANTITY}
      value={quantity}
      disabled={disabled}
      step={packSize}
      data-test-id={"add-to-cart-input-quantity"}
      onValueChange={onChange}
      error={invalidQuantity}
      helperLabel={helperText()}
    />
  );
};

//////////////////////////////////////
// PRICE REFERENCE SECTION
//////////////////////////////////////
type PriceReferenceProps = Pick<Props, "priceReference" | "disabled" | "onPriceReferenceChange"> & {
  product: ProductData;
};
const PriceReferenceSection: FC<PriceReferenceProps> = ({
  priceReference,
  onPriceReferenceChange,
  disabled,
  product,
}) => {
  return (
    <StyledPriceReferenceParam
      product={product}
      priceReference={priceReference}
      // className={"mt-2"}
      onChange={onPriceReferenceChange}
      disabled={disabled}
      data-test-id={"add-to-cart-price-reference"}
      editable={true}
      externalLabel={true}
    />
  );
};

const StyledQuantityInput = styled(QuantityInput)({
  [`& .MuiInputBase-root`]: {
    background: C_ON_PRIMARY,
  },
  [`.MuiButtonBase-root`]: {
    borderRadius: 0,
  },
});

const StyledPriceReferenceParam = styled(PriceReferenceParam)({
  [`& .MuiInputBase-root`]: {
    background: C_ON_PRIMARY,
  },
});

const NewVariantWrapper = styled(Stack)(({ theme }) => ({
  width: "240px",
  [theme.breakpoints.down("tablet")]: {
    width: "100%",
  },
}));

const QuantityDisplay = styled("div")(({ theme }) => ({
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1.25),
}));

const QuantityLabel = styled("span")(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  color: theme.palette.text.secondary,
}));

const QuantityValue = styled("span")(({ theme }) => ({
  color: theme.palette.text.primary,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2.5),
}));
